import axios from "axios";

/** El contexto escucha esto para sincronizar `user` tras refresh (evita token viejo en React). */
export const AUTH_SESSION_UPDATED = "geno-auth-session-updated";
/** Refresh falló o refresh token inválido: cerrar sesión en UI. */
export const AUTH_SESSION_EXPIRED = "geno-auth-session-expired";

let pendingRequests = [];
let refreshInFlight = null;
let proactiveTimer = null;

const processPendingRequests = (error, token = null) => {
  pendingRequests.forEach((promise) => {
    if (error) {
      promise.reject(error);
      return;
    }
    promise.resolve(token);
  });
  pendingRequests = [];
};

const readStoredUser = () => {
  const raw = localStorage.getItem("user");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Solo para programar refresh: lee `exp` del JWT sin verificar firma. */
const decodeJwtExpMs = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(payload));
    const exp = json?.exp;
    if (typeof exp !== "number") return null;
    return exp * 1000;
  } catch {
    return null;
  }
};

const notifySessionUpdated = (userData) => {
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_UPDATED, { detail: userData }));
};

const notifySessionExpired = () => {
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED));
};

/**
 * Renueva access + refresh token y persiste. Lanza si no hay sesión o el refresh falla.
 */
export const refreshAccessToken = async () => {
  const parsedUser = readStoredUser();
  const refreshToken = parsedUser?.refreshToken;
  if (!refreshToken) {
    throw new Error("Sin refresh token");
  }

  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/usuarios/refresh-token`,
    { refreshToken }
  );

  const newToken = response.data?.token;
  const newRefreshToken = response.data?.refreshToken || refreshToken;
  const updatedUsuario = response.data?.usuario;

  if (!newToken) {
    throw new Error("No se recibió un nuevo access token");
  }

  const nextUserData = {
    ...parsedUser,
    ...(updatedUsuario ? { user: updatedUsuario } : {}),
    token: newToken,
    refreshToken: newRefreshToken,
  };

  localStorage.setItem("user", JSON.stringify(nextUserData));
  axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
  notifySessionUpdated(nextUserData);
  return newToken;
};

const getRefreshPromise = () => {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

export const cancelProactiveTokenRefresh = () => {
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
};

/**
 * Programa un refresh ~90s antes de que venza el access token (JWT `exp`).
 * Tras un refresh exitoso, volvé a llamarla (p. ej. desde el listener de sesión).
 */
export const scheduleProactiveTokenRefresh = () => {
  cancelProactiveTokenRefresh();

  const u = readStoredUser();
  if (!u?.token || !u?.refreshToken) return;

  const expMs = decodeJwtExpMs(u.token);
  if (!expMs) return;

  const skewMs = 90_000;
  const delay = Math.max(expMs - Date.now() - skewMs, 5_000);

  proactiveTimer = setTimeout(async () => {
    proactiveTimer = null;
    try {
      await refreshAccessToken();
      scheduleProactiveTokenRefresh();
    } catch {
      localStorage.removeItem("user");
      delete axios.defaults.headers.common.Authorization;
      notifySessionExpired();
    }
  }, delay);
};

export const setupAxiosInterceptors = () => {
  const requestInterceptor = axios.interceptors.request.use((config) => {
    const u = readStoredUser();
    if (u?.token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${u.token}`;
    }
    return config;
  });

  const responseInterceptor = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const statusCode = error?.response?.status;

      if (!originalRequest || statusCode !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      const url = String(originalRequest.url || "");
      if (url.includes("/usuarios/refresh-token") || url.includes("/usuarios/login")) {
        return Promise.reject(error);
      }

      const parsedUser = readStoredUser();
      if (!parsedUser?.refreshToken) {
        return Promise.reject(error);
      }

      if (refreshInFlight) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            originalRequest._retry = true;
            return axios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;

      try {
        const newToken = await getRefreshPromise();
        processPendingRequests(null, newToken);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processPendingRequests(refreshError, null);
        localStorage.removeItem("user");
        delete axios.defaults.headers.common.Authorization;
        notifySessionExpired();
        return Promise.reject(refreshError);
      }
    }
  );

  return () => {
    axios.interceptors.request.eject(requestInterceptor);
    axios.interceptors.response.eject(responseInterceptor);
  };
};
